import React from 'react';
import FeatureIcon from '../../components/FeatureIcon';
import { useTheme } from '../../context/ThemeContext';
import { AdminTab, TABS } from './adminTypes';
import './AdminWorkspace.css';

interface AdminWorkspaceProps {
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  userEmail: string | null;
  onLogout: () => void;
  notification?: { message: string; type: 'error' | 'success' | 'warning' } | null;
  onDismissNotification?: () => void;
  securityNotice?: React.ReactNode;
  children: React.ReactNode;
}

const AdminWorkspace: React.FC<AdminWorkspaceProps> = ({
  activeTab,
  onTabChange,
  userEmail,
  onLogout,
  notification,
  onDismissNotification,
  securityNotice,
  children,
}) => {
  const { colors, dsColors } = useTheme();
  const active = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  const scopeVars = {
    backgroundColor: colors.background,
    '--admin-error': dsColors.error,
    '--admin-success': dsColors.success,
    '--admin-warning': dsColors.warning,
    '--admin-warning-strong': dsColors.warningStrong,
    '--admin-neutral-track': dsColors.treadGray,
    '--admin-primary': dsColors.primaryStrong,
  } as React.CSSProperties;

  return (
    <div className="admin-workspace" style={scopeVars}>
      <header className="admin-topbar" style={{ borderColor: colors.border }}>
        <span className="admin-brand" style={{ color: colors.textPrimary }}>
          RidePulse Admin
        </span>
        <div className="admin-topbar-right">
          {userEmail && (
            <span className="admin-email" style={{ color: colors.textSecondary }}>
              {userEmail}
            </span>
          )}
          <button
            type="button"
            onClick={onLogout}
            className="admin-btn admin-btn-danger"
          >
            Logout
          </button>
        </div>
      </header>

      {notification && (
        <div className="admin-notice-row">
          <div
            className={`admin-notice admin-notice-${notification.type}`}
            role={notification.type === 'error' ? 'alert' : 'status'}
          >
            <span className="admin-notice-message">{notification.message}</span>
            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={onDismissNotification}
              className="admin-notice-dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {securityNotice}

      <div className="admin-body">
        <select
          className="admin-section-select"
          aria-label="Admin section"
          value={activeTab}
          onChange={(e) => onTabChange(e.target.value as AdminTab)}
          style={{ color: colors.textPrimary, borderColor: colors.border, backgroundColor: colors.surface }}
        >
          {TABS.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>

        <div className="admin-layout">
          <nav className="admin-rail" aria-label="Admin sections">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                aria-current={activeTab === t.key ? 'page' : undefined}
                onClick={() => onTabChange(t.key)}
                className="admin-rail-item"
                style={{
                  color: activeTab === t.key ? colors.textPrimary : colors.textSecondary,
                  backgroundColor: activeTab === t.key ? colors.surfaceSecondary : 'transparent',
                }}
              >
                <FeatureIcon name={t.icon} size={18} />
                <span>{t.label}</span>
              </button>
            ))}
          </nav>

          <main className="admin-content">
            <div className="admin-content-head">
              <h1 className="admin-title" style={{ color: colors.textPrimary }}>
                {active.label}
              </h1>
              <p className="admin-context" style={{ color: colors.textSecondary }}>
                {active.context}
              </p>
            </div>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminWorkspace;
